// src/components/HowItWorks.jsx
import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const PANCAKESWAP_URL = "https://pancakeswap.finance/swap?outputCurrency=0xb692e9099a6b275cfc990c994f0d62cca2284444&chain=bsc";

const steps = [
  {
    number: "01",
    title: "Get a Web3 Wallet",
    description: "Use MetaMask, Trust Wallet, or any EVM wallet. Make sure you're on BNB Chain.",
    icon: "/images/howitworks/ConnectWallet_x_512_02.png",
    color: "from-blue-500 to-cyan-500",
  },
  {
    number: "02",
    title: "Get STBOX",
    description: "Purchase tokens via DEX or your wallet. Keep a little BNB for gas fees.",
    icon: "/images/howitworks/Buy_x_512_02.png",
    action: "buy-pancakeswap",
    color: "from-green-500 to-emerald-500",
  },
  {
    number: "03",
    title: "Deposit Tokens",
    description: "Move your STBOX into in-game funds so the game can track your plays, scores, and rewards.",
    icon: "/images/howitworks/Deposit_x_512_02.png",
    color: "from-yellow-500 to-orange-500",
  },
  {
    number: "04",
    title: "Play & Roll",
    description: "Choose your stake and roll the dice. Clear the board for the best score and rewards.",
    icon: "/images/howitworks/Play_x_512_02.png",
    color: "from-pink-500 to-rose-500",
  },
  {
    number: "05",
    title: "Cash Out",
    description: "Withdraw to your wallet anytime. Leaderboard and giveaway pools also pay out here.",
    icon: "/images/howitworks/Cashout_x_512_02.png",
    color: "from-purple-500 to-violet-500",
  },
];

const HowItWorksSection = () => {
  return (
    <section id="how" className="relative w-full py-24 px-4 md:px-8">
      {/* Section Header */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6 }}
        className="text-center mb-16"
      >
        <h2 className="text-4xl md:text-5xl font-bold font-rubik drop-shadow-xl mb-4">
          How It Works
        </h2>
        <div className="w-24 h-1 bg-gradient-to-r from-pink-500 to-purple-500 mx-auto rounded-full mb-4" />
        <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto">
          Five simple steps to start playing Shut The Box on-chain
        </p>
      </motion.div>

      {/* Steps Grid */}
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {steps.map((step, index) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            className="relative group"
          >
            {/* Card */}
            <div className="relative h-full overflow-hidden bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10 p-6 transition-all duration-300 hover:border-white/30 hover:scale-105 hover:shadow-2xl">
              {/* Number Badge */}
              <div className="absolute top-4 right-4">
                <div className="w-6 h-6 rounded-full bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-lg">
                  <span className="text-white font-bold text-[10px]">{step.number}</span>
                </div>
              </div>

              {/* Icon */}
              <div className="mb-4 relative">
                <div className="w-20 h-20 mx-auto relative">
                  <img
                    src={step.icon}
                    alt={step.title}
                    className="w-full h-full object-contain drop-shadow-lg"
                  />
                </div>
              </div>

              {/* Content */}
              <div className="text-center">
                <h3 className="text-xl font-bold mb-3 text-white">{step.title}</h3>
                <p className="text-sm text-white/70 leading-relaxed mb-4">{step.description}</p>

                {/* Direct link to PancakeSwap */}
                {step.action === "buy-pancakeswap" && (
                  <a
                    href={PANCAKESWAP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-[#F0B90B] to-[#ffcc2f] hover:from-[#ffcc2f] hover:to-[#F0B90B] text-black font-semibold text-sm shadow-md transition-all duration-300 hover:shadow-lg hover:scale-105"
                  >
                    Buy on PancakeSwap
                    <ArrowRight className="w-4 h-4" />
                  </a>
                )}
              </div>

              {/* Hover gradient overlay */}
              <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-300`} />
            </div>

            {/* Connector Line (desktop only, not for last item in row) */}
            {index < steps.length - 1 && (
              <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-0.5 bg-gradient-to-r from-white/20 to-transparent -translate-y-1/2 z-10" />
            )}
          </motion.div>
        ))}
      </div>
    </section>
  );
};

export default HowItWorksSection;
